import * as React from 'react';
import { registerComponent, translate } from '@avensia/excite';
import { ContentList } from '@avensia/excite-ninetailed';
import { styled } from '@glitz/react';
import Image from 'webapp/components/image';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import {
  desktopEdgeMarginDecorator,
  edgeMarginsDecorator,
  maxContentExtraNarrowWidthDecorator,
  maxContentNarrowWidthDecorator,
  maxContentNormalWidthDecorator,
  maxContentWideWidthDecorator,
} from 'webapp/components/page-layout';
import { BreadCrumbs } from 'webapp/components/Breadcrumbs';
import { ProductListingLinks } from './ProductListingLinks';
import { isLightshop, isSvenssons } from 'webapp/theme';
import { ShareIcon } from 'webapp/components/icons';

export default registerComponent<NordicNest.Api.InspirationArticlePageApiModel>(props => {
  const onShare = React.useCallback(() => {
    if ('share' in navigator) {
      navigator.share({
        title: __BROWSER__ ? document.title : '',
        text: props.page.ingress,
        url: window.location.href,
      });
    }
  }, [props.page.ingress]);

  const categorySegement = props.breadcrumbs.segments[props.breadcrumbs.segments.length - 2];
  return (
    <Base>
      <BreadCrumbs {...props.breadcrumbs} />
      <TopImageContainer>
        <TopImage image={props.page.largeViewportImage} />
      </TopImageContainer>
      <TopContent>
        {isSvenssons && <Category>{categorySegement.name}</Category>}
        <Title>{props.page.title}</Title>
        <Ingress>{props.page.ingress}</Ingress>
        <DateAndShareContainer>
          <PublishDate>{props.publishDate}</PublishDate>
          <Share onClick={onShare}>
            <Icon /> {translate(t => t.inspiration.shareArticle)}
          </Share>
        </DateAndShareContainer>
      </TopContent>
      <ContentList {...props.page.content} />
      {props.relatedProductListingLinks.length > 0 && <ProductListingLinks links={props.relatedProductListingLinks} />}
    </Base>
  );
});

const Base = styled.div();

const Icon = styled(
  ShareIcon,
  {
    marginRight: t => t.sharedSpacing.xs,
  },
  (isSvenssons || isLightshop) && {
    height: 18,
    width: 18,
  },
);

const TopContent = styled.div(
  edgeMarginsDecorator,
  isSvenssons ? maxContentNormalWidthDecorator : maxContentExtraNarrowWidthDecorator,
  createMinLargeViewportDecorator({ padding: { y: t => t.spacing.xxxl, x: 0 } }),
  {
    padding: { y: t => t.spacing.xl },
  },
);

const TopImage = styled(Image, {
  width: '100%',
});

const TopImageContainer = styled.div(
  isSvenssons ? maxContentNormalWidthDecorator : maxContentWideWidthDecorator,
  isSvenssons && desktopEdgeMarginDecorator,
  {
    display: 'flex',
    justifyContent: 'center',
  },
);

const Title = styled.h1(
  isSvenssons && maxContentNarrowWidthDecorator,
  createMinLargeViewportDecorator({
    fontSize: t => t.sharedFontSize.xxxl,
    marginBottom: t => t.sharedSpacing.xl,
  }),
  {
    fontSize: t => t.sharedFontSize.l,
    marginBottom: t => t.sharedSpacing.m,
    textAlign: 'center',
  },
);

const Category = styled.h3(createMinLargeViewportDecorator({ marginBottom: t => t.spacing.m }), {
  textTransform: 'uppercase',
  fontSize: t => t.fontSize.xs,
  fontWeight: 'normal',
  textAlign: 'center',
  fontFamily: 'charter',
  marginBottom: t => t.spacing.s,
});

const Ingress = styled.div(
  isSvenssons && maxContentNarrowWidthDecorator,
  createMinLargeViewportDecorator({
    fontSize: t => (isSvenssons ? t.fontSize.s : t.fontSize.m),
    lineHeight: '30px',
  }),
  {
    textAlign: 'center',
    fontSize: t => t.fontSize.xs,
    lineHeight: isSvenssons ? '24px' : '22px',
    marginBottom: t => t.sharedSpacing.l,
    fontFamily: t => t.fontFamily?.primary,
  },
  isSvenssons &&
    createMinLargeViewportDecorator({
      marginBottom: t => t.spacing.xl,
    }),
);

const DateAndShareContainer = styled.div(
  createMinLargeViewportDecorator({
    fontSize: t => t.fontSize.xs,
  }),
  {
    display: 'flex',
    justifyContent: 'center',
    fontSize: t => t.fontSize.xxs,
  },
);

const PublishDate = styled.div({
  color: t => t.dark.color,
  marginRight: t => t.spacing.l,
});

const Share = styled.div({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});
